/**
 * Clara 360 - Rent Payments Service
 * Handles rent payment tracking, confirmation, and reconciliation
 */

import { masterSupabase as supabase } from '../lib/supabase-master.js';

class RentPaymentsService {
  constructor() {
    this.currentUser = null;
  }

  // ============================================================
  // AUTHENTICATION & SESSION
  // ============================================================
  
  async ensureValidSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session?.access_token) {
        throw new Error('Benutzer nicht authentifiziert');
      }
      
      return session;
    } catch (error) {
      console.error('❌ Session-Fehler:', error);
      throw error;
    }
  }

  async getCurrentUser() {
    try {
      await this.ensureValidSession();
      
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) {
        throw new Error('Benutzer nicht authentifiziert');
      }
      
      this.currentUser = user;
      return user;
    } catch (error) {
      console.error('❌ Fehler bei User-Ermittlung:', error);
      throw error;
    }
  }

  // ============================================================
  // MONTH AGGREGATION (Primary API)
  // ============================================================
  
  /**
   * Get rent payment overview for a specific month
   * Returns aggregated data + individual tenant rows
   */
  async getMonthOverview(year, month, options = {}) {
    try {
      const session = await this.ensureValidSession();
      
      // ── Capital-Bankdaten über Backend-Proxy ─────────────────────────────
      // Liest echte Zahlungseingänge aus Capital MySQL (matching_engine_v2)
      // statt aus der veralteten Supabase-View rent_payments_with_status
      const token = session?.access_token;
      const testParam = options.includeTest ? '&include_test=1' : '';
      const res = await fetch(`/api/rent/overview?year=${year}&month=${month}${testParam}`, {
        headers: {
          'x-supabase-access-token': token,
          'Content-Type': 'application/json'
        }
      });
      
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Capital API Fehler: ${res.status} ${errText}`);
      }
      
      const data = await res.json();
      const payments = data.payments || [];
      const specialPayments = data.special_payments || [];
      const unassignedObjectCandidates = data.unassigned_object_candidates || [];
      const unassignedPaymentCandidates = data.unassigned_payment_candidates || [];
      
      // Summary kommt direkt von Capital, Fallback-Berechnung falls nötig
      const summary = data.summary || {
        expected: payments.reduce((sum, p) => sum + parseFloat(p.expected_amount || 0), 0),
        received: payments.reduce((sum, p) => sum + parseFloat(p.received_amount || 0), 0),
        open: 0,
        quote: 0,
        paid_count: payments.filter(p => p.derived_state === 'paid').length,
        partial_count: payments.filter(p => p.derived_state === 'partial').length,
        overdue_count: payments.filter(p => p.derived_state === 'overdue').length,
        open_count: payments.filter(p => p.derived_state === 'open').length,
        total_count: payments.length
      };
      
      if (!summary.open) summary.open = Math.max(0, summary.expected - summary.received);
      if (!summary.quote) summary.quote = summary.expected > 0 ? summary.received / summary.expected : 0;
      
      return {
        month: `${year}-${String(month).padStart(2, '0')}`,
        summary,
        rows: payments,
        special_payments: specialPayments,
        unassigned_object_candidates: unassignedObjectCandidates,
        unassigned_payment_candidates: unassignedPaymentCandidates,
        data_source: 'capital'
      };
    } catch (error) {
      console.error('❌ Fehler beim Laden der Monatsübersicht (Capital):', error);
      // Fallback auf Supabase wenn Capital nicht erreichbar
      try {
        const { data: payments, error: sbErr } = await supabase
          .from('rent_payments_with_status')
          .select('*')
          .eq('payment_year', year)
          .eq('payment_month', month)
          .order('property_name', { ascending: true })
          .order('tenant_name', { ascending: true });
        if (sbErr) throw sbErr;
        const summary = {
          expected: payments.reduce((sum, p) => sum + parseFloat(p.expected_amount || 0), 0),
          received: payments.reduce((sum, p) => sum + parseFloat(p.received_amount || 0), 0),
          open: 0, quote: 0,
          paid_count: payments.filter(p => p.derived_state === 'paid').length,
          partial_count: payments.filter(p => p.derived_state === 'partial').length,
          overdue_count: payments.filter(p => p.derived_state === 'overdue').length,
          open_count: payments.filter(p => p.derived_state === 'open').length,
          total_count: payments.length
        };
        summary.open = summary.expected - summary.received;
        summary.quote = summary.expected > 0 ? summary.received / summary.expected : 0;
        return {
          month: `${year}-${String(month).padStart(2, '0')}`,
          summary,
          rows: payments,
          special_payments: [],
          unassigned_object_candidates: [],
          unassigned_payment_candidates: [],
          data_source: 'supabase_fallback'
        };
      } catch (fbErr) {
        throw error; // Ursprünglichen Fehler weiterwerfen
      }
    }
  }

  // ============================================================
  // READ-ONLY TENANT LEDGER
  // ============================================================
  // ============================================================
  // DUNNING CLAUSE CHECK / CONFIRM (Anzeige + menschliche Übernahme)
  // ============================================================
  async checkDunningClause({ propertyId, unitId, tenantName, year }) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/dunning-clause-check', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''), year: Number(year || new Date().getFullYear()) })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Mahnkosten-Check nicht verfügbar (HTTP ${response.status})`);
    return payload;
  }
  async confirmDunningClause({ propertyId, unitId, tenantName, documentId, findingType, quote }) {
    const session = await this.ensureValidSession();
    const user = await this.getCurrentUser().catch(() => null);
    const response = await fetch('/api/rent/dunning-clause/confirm', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''), document_id: String(documentId || ''), finding_type: String(findingType || ''), quote: String(quote || ''), confirmation: true, reviewed_by: user?.email || 'human_confirm' })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Klausel-Übernahme fehlgeschlagen (HTTP ${response.status})`);
    return payload;
  }
  // ============================================================
  // LETTER (Mahnbrief: Entwurf erzeugen, ablegen – beides nur auf Klick)
  // ============================================================
  async createNkSettlementPdf({ propertyId, unitId, organizationId, billingYear, tenantName }) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/nk/unit-settlement-pdf', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        property_id: String(propertyId || ''),
        unit_id: String(unitId || ''),
        organization_id: String(organizationId || ''),
        billing_year: Number(billingYear || new Date().getFullYear()),
        tenant_name: String(tenantName || ''),
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.error || 'settlement_pdf_failed');
    return data;
  }
  async createLetterDraft({ propertyId, unitId, tenantName, year, months, subject, purpose, salutation, mahnkosten }) {
    const session = await this.ensureValidSession();
    const user = await this.getCurrentUser().catch(() => null);
    const query = new URLSearchParams({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''), organization_id: '', year: String(year || new Date().getFullYear()) });
    const response = await fetch('/api/rent/letter/draft', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''), year: Number(year || new Date().getFullYear()), months: months || [], subject: subject || '', purpose: purpose || '', salutation: String(salutation || '').trim(), mahnkosten: Number(mahnkosten || 0) })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Briefentwurf fehlgeschlagen (HTTP ${response.status})`);
    return payload;
  }
  async fileLetter({ propertyId, unitId, tenantName, filename, pdfBase64 }) {
    const session = await this.ensureValidSession();
    const user = await this.getCurrentUser().catch(() => null);
    const response = await fetch('/api/rent/letter/file', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''), filename: String(filename || ''), pdf_base64: String(pdfBase64 || ''), reviewed_by: user?.email || 'human_file' })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Briefablage fehlgeschlagen (HTTP ${response.status})`);
    return payload;
  }
  async getTenantLedger({ propertyId, unitId, tenantName, year }) {
    const session = await this.ensureValidSession();
    const query = new URLSearchParams({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''), year: String(year || new Date().getFullYear()) });
    const response = await fetch(`/api/rent/tenant-ledger?${query.toString()}`, {
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Mietchronik nicht verfügbar (HTTP ${response.status})`);
    return payload;
  }

  // ============================================================
  // READ-ONLY BMF NK ANNUAL RECEIVABLE MONITOR
  // ============================================================
  async getNkAnnualReceivables({ propertyId, billingYear = 2024 }) {
    const session = await this.ensureValidSession();
    const query = new URLSearchParams({ property_id: String(propertyId || ''), billing_year: String(billingYear) });
    const response = await fetch(`/api/rent/nk-annual-receivables?${query.toString()}`, {
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `NK-Jahresabrechnungsstatus nicht verfügbar (HTTP ${response.status})`);
    return payload;
  }

  // ============================================================
  // READ-ONLY RENT / NK CONTRACT HISTORY
  // ============================================================
  async getTenantContractHistory({ propertyId, unitId, tenantName }) {
    const session = await this.ensureValidSession();
    const query = new URLSearchParams({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || '') });
    const response = await fetch(`/api/rent/contract-history?${query.toString()}`, {
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Miet- und NK-Historie nicht verfügbar (HTTP ${response.status})`);
    return payload;
  }

  async getPropertyContractHistoryIndex({ propertyId }) {
    const session = await this.ensureValidSession();
    const query = new URLSearchParams({ property_id: String(propertyId || '') });
    const response = await fetch(`/api/rent/contract-history/index?${query.toString()}`, {
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Miet- und NK-Objektübersicht nicht verfügbar (HTTP ${response.status})`);
    return payload;
  }

  async getContractHistoryProperties() {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/contract-history/properties', {
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Objektliste für Miet- und NK-Historie nicht verfügbar (HTTP ${response.status})`);
    return payload;
  }

  // BMF Canary: ausschließlich lesende Differenzanalyse zwischen dem aktuellen
  // Capital-Snapshot und zeitbezogenen Vertragsfakten. Der Endpunkt enthält
  // bewusst keinen Apply- oder Änderungsmechanismus.
  async getRentConditionApplyPreview({ propertyId, unitId, tenantName }) {
    const session = await this.ensureValidSession();
    const query = new URLSearchParams({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || '') });
    const response = await fetch(`/api/rent/condition-apply-preview?${query.toString()}`, {
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Konditionsvorschau nicht verfügbar (HTTP ${response.status})`);
    return payload;
  }

  // BMF Canary: Ein belegter Konditionsentwurf bleibt von der Mieteingangskontrolle
  // getrennt. Erst eine serverseitig protokollierte zweite Freigabe kann später eine
  // separate Apply-Stufe ermöglichen; diese Serviceklasse besitzt keinen Apply-Aufruf.
  async getRentConditionChanges({ propertyId, unitId, tenantName }) {
    const session = await this.ensureValidSession();
    const query = new URLSearchParams({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || '') });
    const response = await fetch(`/api/rent/condition-change?${query.toString()}`, {
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Konditionsvorgänge nicht verfügbar (HTTP ${response.status})`);
    return payload;
  }

  async createRentConditionChange(payload) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/condition-change', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {})
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body?.error || `Konditionsentwurf konnte nicht gespeichert werden (HTTP ${response.status})`);
    return body;
  }

  async transitionRentConditionChange({ requestId, action, note = '' }) {
    if (!['submit', 'approve', 'reject'].includes(String(action || ''))) throw new Error('Ungültige Konditionsaktion');
    const session = await this.ensureValidSession();
    const response = await fetch(`/api/rent/condition-change/${encodeURIComponent(String(requestId || ''))}/${action}`, {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: String(note || '') })
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body?.error || `Konditionsaktion konnte nicht ausgeführt werden (HTTP ${response.status})`);
    return body;
  }

  async getTenantContractHistoryDocument({ propertyId, documentId }) {
    const session = await this.ensureValidSession();
    const query = new URLSearchParams({ property_id: String(propertyId || ''), document_id: String(documentId || '') });
    const response = await fetch(`/api/rent/contract-history/document?${query.toString()}`, {
      headers: { 'x-supabase-access-token': session.access_token }
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.error || `Dokument nicht verfügbar (HTTP ${response.status})`);
    }
    return { blob: await response.blob(), filename: response.headers.get('content-disposition') || 'Miet-NK-Beleg' };
  }

  // Eine einzelne Sollgrundlage wird erst nach dem finalen UI-Klick bestätigt.
  // Der Service schreibt nur einen append-only Contract-Event; Banktransaktion,
  // Zielperiode, Zahlungsstatus, Saldo und Kommunikation bleiben unverändert.
  async confirmTenantLedgerRentBasis({ propertyId, unitId, tenantName, transactionId, targetPeriod, expectedAmountEur }) {
    const session = await this.ensureValidSession();
    const reviewedBy = String(session?.user?.id || session?.user?.email || '').trim();
    if (!reviewedBy) throw new Error('Angemeldeter Prüfer konnte nicht ermittelt werden.');
    const response = await fetch('/api/rent/tenant-ledger/confirm-rent-basis', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''), transaction_id: String(transactionId || ''), target_period: String(targetPeriod || ''), expected_amount_eur: Number(expectedAmountEur), reviewed_by: reviewedBy, confirmation: true })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Sollgrundlage konnte nicht bestätigt werden (HTTP ${response.status})`);
    return payload;
  }

  // Erster menschlicher Prüfschritt für gemeinsame Bankquellen: ausschließlich Objekt.
  // Mieter, Einheit, Zielmonat, Zahlungsstatus, Miet-Ist und Saldo bleiben offen.
  async confirmObjectAssignment({ propertyId, transactionId }) {
    const session = await this.ensureValidSession();
    const reviewedBy = String(session?.user?.id || session?.user?.email || '').trim();
    if (!reviewedBy) throw new Error('Angemeldeter Prüfer konnte nicht ermittelt werden.');
    const response = await fetch('/api/rent/tenant-ledger/confirm-object-assignment', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ property_id: String(propertyId || ''), transaction_id: String(transactionId || ''), reviewed_by: reviewedBy, confirmation: true })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Objektzuordnung konnte nicht bestätigt werden (HTTP ${response.status})`);
    return payload;
  }

  // Zweiter menschlicher Prüfschritt: ausschließlich Einheit und Mieter im bereits
  // bestätigten Objekt. Zielmonat, Zahlungsstatus, Miet-Ist und Saldo bleiben offen.
  async confirmTenantAssignment({ propertyId, unitId, tenantName, transactionId }) {
    const session = await this.ensureValidSession();
    const reviewedBy = String(session?.user?.id || session?.user?.email || '').trim();
    if (!reviewedBy) throw new Error('Angemeldeter Prüfer konnte nicht ermittelt werden.');
    const response = await fetch('/api/rent/tenant-ledger/confirm-tenant-assignment', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''), transaction_id: String(transactionId || ''), reviewed_by: reviewedBy, confirmation: true })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Mieterzuordnung konnte nicht bestätigt werden (HTTP ${response.status})`);
    return payload;
  }

  // Eine einzelne Zielperiode wird erst nach dem finalen UI-Klick bestätigt.
  // Capital prüft nochmals Tenant, Einheit, belegten Sollbetrag und den exakten Bankbetrag.
  async confirmTenantLedgerPeriod({ propertyId, unitId, tenantName, transactionId, targetPeriod, confirmationType = 'full_payment' }) {
    const session = await this.ensureValidSession();
    const reviewedBy = String(session?.user?.id || session?.user?.email || '').trim();
    if (!reviewedBy) throw new Error('Angemeldeter Prüfer konnte nicht ermittelt werden.');
    const response = await fetch('/api/rent/tenant-ledger/confirm-period', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''), transaction_id: String(transactionId || ''), target_period: String(targetPeriod || ''), confirmation_type: confirmationType, reviewed_by: reviewedBy, confirmation: true })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Belegzuordnung konnte nicht bestätigt werden (HTTP ${response.status})`);
    return payload;
  }

  // Miete + Kaution (16.10.): Misch-Zahlung per Button aufteilen.
  // Mietanteil zaehlt in den Zahlungsverlauf, Kautionsanteil zu "Meine Mieter".
  // Keine Automatik, keine Verrechnung, keine Buchung.
  async confirmMieteKaution({ propertyId, unitId, tenantName, transactionId, targetPeriod, mieteAmountEur, kautionAmountEur }) {
    const session = await this.ensureValidSession();
    const reviewedBy = String(session?.user?.id || session?.user?.email || '').trim();
    if (!reviewedBy) throw new Error('Angemeldeter Prüfer konnte nicht ermittelt werden.');
    const response = await fetch('/api/rent/tenant-ledger/confirm-miete-kaution', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''),
        transaction_id: String(transactionId || ''), target_period: String(targetPeriod || ''),
        miete_amount_eur: Number(mieteAmountEur || 0), kaution_amount_eur: Number(kautionAmountEur || 0),
        reviewed_by: reviewedBy,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Aufteilung Miete + Kaution nicht bestätigt (HTTP ${response.status})`);
    return payload;
  }

  // Kaution fuer "Meine Mieter" (nur lesen, keine Verrechnung)
  async getTenantDeposit({ propertyId, unitId }) {
    const session = await this.ensureValidSession();
    const query = new URLSearchParams({ property_id: String(propertyId || ''), unit_id: String(unitId || '') });
    const response = await fetch(`/api/rent/tenant/deposit?${query.toString()}`, {
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Kaution nicht verfügbar (HTTP ${response.status})`);
    return payload;
  }

  // ============================================================
  // READ-ONLY CONTRACT RULE RESOLVER
  // ============================================================
  async getContractRule({ propertyId, unitId, tenantName, year }) {
    const session = await this.ensureValidSession();
    const query = new URLSearchParams({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''), year: String(year || new Date().getFullYear()) });
    const response = await fetch(`/api/rent/contract-rule?${query.toString()}`, {
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Vertragsregel nicht verfügbar (HTTP ${response.status})`);
    return payload;
  }

  // Führt ausschließlich einen lokalen, read-only Scan bekannter Vertragsquellen aus.
  // Der Endpunkt legt keine Vertragsfakten an und darf weder Forderungen noch Mahnkosten verändern.
  async scanContractRule({ propertyId, unitId, tenantName, year }) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/contract-rule-scan', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''), year: String(year || new Date().getFullYear()) })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Mietvertrags-Scan nicht verfügbar (HTTP ${response.status})`);
    return payload;
  }

  // ============================================================
  // READ-ONLY DUNNING HISTORY — nur erfolgreich auditierte Zustellungen
  // ============================================================
  async getDunningHistory({ propertyId, unitId, tenantId }) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/dunning-history', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_id: String(tenantId || '') })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Mahnverlauf nicht verfügbar (HTTP ${response.status})`);
    return payload;
  }

  // ============================================================
  // TEST CANARY ACTIONS — nur testmarkierte Capital-Zahlungen
  // ============================================================
  async performTestAction(action, transactionId, amount = undefined) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/test-action', {
      method: 'POST',
      headers: {
        'x-supabase-access-token': session.access_token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ action, transaction_id: transactionId, amount })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || ('Testaktion nicht verfügbar (HTTP ' + response.status + ')'));
    return payload;
  }

  // ============================================================
  // TEST EMAIL CONTACT + FINAL TEST DELIVERY — nur Canary
  // ============================================================
  async testEmailConfig(action, transactionId, payload = {}) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/test-email-config', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, transaction_id: transactionId, ...payload })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.error || ('Testkontakt nicht verfügbar (HTTP ' + response.status + ')'));
    return result;
  }

  async getTestEmailContact(transactionId) {
    return this.testEmailConfig('get_contact', transactionId);
  }

  async validateTestWhatsAppLocal({ transactionId, phone, message, snapshot, includePdf = true }) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/test-whatsapp-local-validate', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ transaction_id: transactionId, phone, message, snapshot, include_pdf: includePdf })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.error || ('Lokaler WhatsApp-Test nicht verfügbar (HTTP ' + response.status + ')'));
    return result;
  }

  async startTestEmailReview({ transactionId, email, subject, message, snapshot, includePdf = false }) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/test-email-review', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ transaction_id: transactionId, email, subject, message, snapshot, include_pdf: includePdf })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.error || ('Überprüfung nicht verfügbar (HTTP ' + response.status + ')'));
    return result;
  }

  async sendTestEmail({ transactionId, email, subject, message, snapshot, reviewToken, includePdf = false }) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/test-email-send', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ transaction_id: transactionId, email, subject, message, snapshot, review_token: reviewToken, include_pdf: includePdf })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.error || ('Test-E-Mail nicht verfügbar (HTTP ' + response.status + ')'));
    return result;
  }

  async productionEmailContact(action, tenantId, payload = {}) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/production-email-contact', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, tenant_id: tenantId, ...payload })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.error || ('Mieterkontakt nicht verfügbar (HTTP ' + response.status + ')'));
    return result;
  }

  async startProductionEmailReview({ tenantId, transactionId, email, recipientConfirmed, subject, message, snapshot, includePdf = true }) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/production-email-review', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenant_id: tenantId, transaction_id: transactionId, email, recipient_confirmed: recipientConfirmed, subject, message, snapshot, include_pdf: includePdf })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.error || ('Überprüfung nicht verfügbar (HTTP ' + response.status + ')'));
    return result;
  }

  async sendProductionEmail({ tenantId, transactionId, email, recipientConfirmed, subject, message, snapshot, reviewToken, includePdf = true }) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/production-email-send', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenant_id: tenantId, transaction_id: transactionId, email, recipient_confirmed: recipientConfirmed, subject, message, snapshot, review_token: reviewToken, include_pdf: includePdf })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.error || ('E-Mail nicht verfügbar (HTTP ' + response.status + ')'));
    return result;
  }

  // ============================================================
  // PAYMENT CONFIRMATION
  // ============================================================
  
  /**
   * Confirm a payment (mark as received)
   */
  async confirmPayment(paymentId, amount, paymentDate = null) {
    try {
      await this.ensureValidSession();
      const user = await this.getCurrentUser();
      
      const updates = {
        received_amount: amount,
        payment_date: paymentDate || new Date().toISOString().split('T')[0],
        confirmed_at: new Date().toISOString(),
        status: 'closed',
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('rent_payments')
        .update(updates)
        .eq('id', paymentId)
        .select()
        .single();
      
      if (error) throw error;
      
      // Log action
      await this.logAction({
        entity_type: 'rent_payment',
        entity_id: paymentId,
        action: 'confirmed',
        payload: {
          received_amount: amount,
          payment_date: paymentDate,
          confirmed_by: user.id
        },
        actor: user.id
      });
      
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Bestätigen der Zahlung:', error);
      throw error;
    }
  }

  /**
   * Quick confirm (full amount, today)
   */
  async quickConfirm(paymentId, expectedAmount) {
    return this.confirmPayment(paymentId, expectedAmount, new Date().toISOString().split('T')[0]);
  }

  /**
   * Partial payment
   */
  async recordPartialPayment(paymentId, partialAmount, paymentDate = null) {
    try {
      await this.ensureValidSession();
      const user = await this.getCurrentUser();
      
      // Get current payment
      const { data: current, error: fetchError } = await supabase
        .from('rent_payments')
        .select('received_amount')
        .eq('id', paymentId)
        .single();
      
      if (fetchError) throw fetchError;
      
      const newTotal = parseFloat(current.received_amount || 0) + parseFloat(partialAmount);
      
      const updates = {
        received_amount: newTotal,
        payment_date: paymentDate || new Date().toISOString().split('T')[0],
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('rent_payments')
        .update(updates)
        .eq('id', paymentId)
        .select()
        .single();
      
      if (error) throw error;
      
      // Log action
      await this.logAction({
        entity_type: 'rent_payment',
        entity_id: paymentId,
        action: 'partial_payment',
        payload: {
          partial_amount: partialAmount,
          new_total: newTotal,
          payment_date: paymentDate
        },
        actor: user.id
      });
      
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Erfassen der Teilzahlung:', error);
      throw error;
    }
  }

  // ============================================================
  // BOOTSTRAP (Auto-create payments for month)
  // ============================================================
  
  async bootstrapMonth(year, month) {
    try {
      await this.ensureValidSession();
      
      const { data, error } = await supabase.rpc('bootstrap_rent_payments_for_month', {
        target_year: year,
        target_month: month
      });
      
      if (error) throw error;
      
      console.log(`✅ Bootstrap: ${data} Zahlungen erstellt für ${year}-${month}`);
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Bootstrap:', error);
      throw error;
    }
  }

  // ============================================================
  // CORRECTION & REOPENING
  // ============================================================
  
  async correctPayment(paymentId, newAmount, reason) {
    try {
      await this.ensureValidSession();
      const user = await this.getCurrentUser();
      
      // Get old value for audit
      const { data: old, error: fetchError } = await supabase
        .from('rent_payments')
        .select('received_amount')
        .eq('id', paymentId)
        .single();
      
      if (fetchError) throw fetchError;
      
      const updates = {
        received_amount: newAmount,
        payment_source: 'correction',
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('rent_payments')
        .update(updates)
        .eq('id', paymentId)
        .select()
        .single();
      
      if (error) throw error;
      
      // Log correction
      await this.logAction({
        entity_type: 'rent_payment',
        entity_id: paymentId,
        action: 'corrected',
        payload: {
          old_received: old.received_amount,
          new_received: newAmount,
          reason: reason
        },
        actor: user.id
      });
      
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Korrigieren:', error);
      throw error;
    }
  }

  async reopenPayment(paymentId, reason) {
    try {
      await this.ensureValidSession();
      const user = await this.getCurrentUser();
      
      const updates = {
        status: 'open',
        received_amount: 0,
        payment_date: null,
        confirmed_at: null,
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('rent_payments')
        .update(updates)
        .eq('id', paymentId)
        .select()
        .single();
      
      if (error) throw error;
      
      // Log reopening
      await this.logAction({
        entity_type: 'rent_payment',
        entity_id: paymentId,
        action: 'reopened',
        payload: { reason },
        actor: user.id
      });
      
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Wiedereröffnen:', error);
      throw error;
    }
  }

  // ============================================================
  // ACTION LOG
  // ============================================================
  
  async logAction({ entity_type, entity_id, action, payload, actor }) {
    try {
      const { error } = await supabase
        .from('action_log')
        .insert({
          entity_type,
          entity_id,
          action,
          payload,
          actor
        });
      
      if (error) {
        console.warn('⚠️ Action-Log fehlgeschlagen:', error);
        // Don't throw - logging failure shouldn't break main operation
      }
    } catch (error) {
      console.warn('⚠️ Action-Log Exception:', error);
    }
  }

  async getPaymentHistory(paymentId) {
    try {
      const { data, error } = await supabase
        .from('action_log')
        .select('*')
        .eq('entity_type', 'rent_payment')
        .eq('entity_id', paymentId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Historie:', error);
      return [];
    }
  }

  // ============================================================
  // YEAR OVERVIEW (for analytics)
  // ============================================================
  
  async getYearOverview(year) {
    try {
      await this.ensureValidSession();
      
      const { data, error } = await supabase
        .from('rent_payments_with_status')
        .select('*')
        .eq('payment_year', year)
        .order('payment_month', { ascending: true });
      
      if (error) throw error;
      
      // Group by month
      const monthlyData = {};
      for (let month = 1; month <= 12; month++) {
        const monthPayments = data.filter(p => p.payment_month === month);
        
        monthlyData[month] = {
          expected: monthPayments.reduce((sum, p) => sum + parseFloat(p.expected_amount || 0), 0),
          received: monthPayments.reduce((sum, p) => sum + parseFloat(p.received_amount || 0), 0),
          count: monthPayments.length
        };
      }
      
      return monthlyData;
    } catch (error) {
      console.error('❌ Fehler beim Laden der Jahresübersicht:', error);
      throw error;
    }
  }

  // ============================================================
  // SYNC EXPECTED AMOUNTS
  // ============================================================
  
  async syncExpectedAmounts(year) {
    try {
      await this.ensureValidSession();
      
      // Call the database function
      const { data, error } = await supabase
        .rpc('sync_rent_payments_with_tenants', {
          target_year: year,
          only_open: true
        });
      
      if (error) throw error;
      
      // RPC returns array with one row: [{ updated_count: 3 }]
      return {
        updated_count: data?.[0]?.updated_count || 0
      };
    } catch (error) {
      console.error('❌ Fehler beim Sync:', error);
      throw error;
    }
  }

  // ============================================================
  // MIETER-HINWEISE (z.B. Mietminderung) - reine Information.
  // Keine Forderungswirkung, keine Verrechnung, keine Buchung.
  // ============================================================
  async getTenantNotes({ propertyId, unitId }) {
    const session = await this.ensureValidSession();
    const response = await fetch(`/api/rent/tenant-notes?property_id=${encodeURIComponent(propertyId || '')}&unit_id=${encodeURIComponent(unitId || '')}`, {
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Mieter-Hinweise konnten nicht geladen werden (HTTP ${response.status})`);
    return payload;
  }

  async createTenantNote({ propertyId, unitId, tenantName, category, noteText, reason, periodStart, periodEnd, percent, amountEur }) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/tenant-notes', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        property_id: String(propertyId || ''), unit_id: String(unitId || ''), tenant_name: String(tenantName || ''),
        category: String(category || 'sonstiges'), note_text: String(noteText || ''), reason: String(reason || ''),
        period_start: periodStart || null, period_end: periodEnd || null,
        percent: percent == null ? null : percent, amount_eur: amountEur == null ? null : amountEur,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Mieter-Hinweis nicht gespeichert (HTTP ${response.status})`);
    return payload;
  }

  async deactivateTenantNote({ id }) {
    const session = await this.ensureValidSession();
    const response = await fetch('/api/rent/tenant-notes/deactivate', {
      method: 'POST',
      headers: { 'x-supabase-access-token': session.access_token, 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(id || '') }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || `Mieter-Hinweis nicht deaktiviert (HTTP ${response.status})`);
    return payload;
  }
}
// Export singleton instance
export const rentPaymentsService = new RentPaymentsService();
export default rentPaymentsService;
